import { useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import memLogoUrl from "../../../assets/icons/logo/logo-rounded.svg";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { api } from "@/modules/api";
import { getBackendApiErrorMessage } from "@/modules/api/errorHandling";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";
import { parseCommercialUsageLimitRejection } from "@/modules/commercial-plans";
import { DeleteKeyModal } from "@/pages/integrations/api/integrations-api-page-delete-modal";
import {
  trackMcpConnectionSetupAttempt,
  trackMcpConnectionSetupNetworkFailure,
} from "@/pages/settings/mcp-connection-observability";
import { getComposioConnectionApp } from "../composio-connection-apps";
import { DeleteTemplateModal } from "../../../components/modal/delete-template";

import { DeleteAccountModal } from "./delete-account-modal";
import { SettingsRemindersComingSoonOverlay } from "./settings-reminders-coming-soon-overlay";

export const SettingsModalOverlays = observer(() => {
  const { pageStore, store } = useAppStore();
  const accountManagementPage = pageStore.settingsAccountManagementPage;
  const subscriptionManagementPage = pageStore.settingsSubscriptionManagementPage;
  const templatesPage = store.settingsModalTemplatesPage;

  return (
    <>
      <SettingsRemindersComingSoonOverlay />
      <DeleteKeyModal />
      <DeleteTemplateModal store={templatesPage.deleteTemplateModal} />
      <McpConnectionDisconnectDialog />
      <McpComposioConnectionConfirmationDialog />

      <DeleteAccountModal
        isOpen={accountManagementPage.isDeleteAccountModalOpen}
        handleClose={() => accountManagementPage.closeDeleteAccountModal()}
      />

      <MDSModalDialog
        isOpen={subscriptionManagementPage.isAppleManagedDialogOpen}
        handleClose={() => subscriptionManagementPage.closeAppleManagedDialog()}
      >
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Manage via App Store
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Your subscription was purchased through the Apple App Store. To manage your plan, please
            use your App Store subscription settings.
          </MDSText>
        </MDSDialogTextContent>
        <div slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton onClick={() => subscriptionManagementPage.closeAppleManagedDialog()}>
            Close
          </MDSButton>
        </div>
      </MDSModalDialog>
    </>
  );
});

const MCP_CONNECTION_NETWORK_ERROR_MESSAGE =
  "We couldn’t start the connection. Check your internet connection and try again.";

const McpComposioConnectionConfirmationDialog = observer(() => {
  const { store } = useAppStore();
  const pending = store.settingsModal.mcpComposioConnectionPendingConfirmation;
  const [isConnecting, setIsConnecting] = useState(false);
  const appTitle = pending?.appTitle;
  const app = pending ? getComposioConnectionApp(pending.toolkit) : undefined;

  const handleClose = () => {
    if (!isConnecting) {
      store.settingsModal.closeMcpComposioConnectionConfirmation();
    }
  };

  const handleContinue = async () => {
    if (!pending) return;

    setIsConnecting(true);
    trackMcpConnectionSetupAttempt();
    try {
      const response = await api.post("/v2/mcp-connections/composio", {
        body: { toolkit: pending.toolkit },
      });
      if (response.error || !response.data) {
        const usageLimitRejection = parseCommercialUsageLimitRejection(response.error);
        if (usageLimitRejection) {
          store.settingsModal.closeMcpComposioConnectionConfirmation();
          store.subscriptions.openCommercialUsageLimit(usageLimitRejection);
          return;
        }

        throw new Error(
          getBackendApiErrorMessage(response.error) ?? "Could not connect this service."
        );
      }

      store.settingsModal.closeMcpComposioConnectionConfirmation();
      if (response.data.authorization_url) {
        await windowModule.navigateToExternalAndPreserveHistory({
          url: new URL(response.data.authorization_url),
        });
      }
    } catch (error) {
      if (error instanceof TypeError) {
        trackMcpConnectionSetupNetworkFailure();
      }
      toastModule.triggerToast({
        content:
          error instanceof TypeError
            ? MCP_CONNECTION_NETWORK_ERROR_MESSAGE
            : error instanceof Error
              ? error.message
              : "Could not connect this service.",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <MDSModalDialog
      isOpen={pending !== null && pending !== undefined}
      isDismissable={!isConnecting}
      handleClose={handleClose}
    >
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Connect {appTitle} to Mem
      </MDSText>
      <StyledComposioDialogContent slot={MDSDialogSlotName.Content}>
        <StyledConnectionPreview aria-label={`Mem connects to ${appTitle} through Composio`}>
          <StyledConnectionApp>
            <StyledConnectionLogo src={memLogoUrl} alt="Mem" />
            <MDSText variant={MDSTextVariant.TextXs}>Mem</MDSText>
          </StyledConnectionApp>
          <StyledConnectionArrow aria-hidden="true">→</StyledConnectionArrow>
          <StyledConnectionApp>
            {app ? <StyledConnectionLogo src={app.iconUrl} alt={appTitle} /> : null}
            <MDSText variant={MDSTextVariant.TextXs}>{appTitle}</MDSText>
          </StyledConnectionApp>
        </StyledConnectionPreview>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          Mem uses Composio, our connection partner, to securely connect to {appTitle}.
        </MDSText>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          Next, you’ll continue to Composio to sign in and review the permissions before anything is
          connected.
        </MDSText>
      </StyledComposioDialogContent>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton isDisabled={isConnecting} onClick={handleClose}>
          Cancel
        </MDSButton>
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          isLoading={isConnecting}
          onClick={() => void handleContinue()}
        >
          Continue to Composio
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
});

const StyledComposioDialogContent = styled(MDSDialogTextContent)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const StyledConnectionPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.card.default};
`;

const StyledConnectionApp = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 72px;
`;

const StyledConnectionLogo = styled.img`
  width: 44px;
  height: 44px;
  object-fit: contain;
`;

const StyledConnectionArrow = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 24px;
  line-height: 1;
`;

const McpConnectionDisconnectDialog = observer(() => {
  const { store } = useAppStore();
  const connection = store.settingsModal.mcpConnectionPendingDisconnect;
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const handleClose = () => {
    if (!isDisconnecting) {
      store.settingsModal.closeMcpConnectionDisconnectDialog();
    }
  };

  const handleDisconnect = async () => {
    if (!connection) return;

    setIsDisconnecting(true);
    try {
      const response = await api.delete("/v2/mcp-connections/{connection_id}", {
        params: { path: { connection_id: connection.id } },
      });
      if (response.error) {
        throw new Error(
          getBackendApiErrorMessage(response.error) ?? "Could not disconnect this server."
        );
      }

      store.settingsModal.closeMcpConnectionDisconnectDialog();
      toastModule.triggerToast({ content: "Disconnected" });
    } catch (error) {
      toastModule.triggerToast({
        content: error instanceof Error ? error.message : "Could not disconnect this server.",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <MDSModalDialog
      isOpen={connection !== null && connection !== undefined}
      isDismissable={!isDisconnecting}
      handleClose={handleClose}
    >
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        {`Disconnect ${
          connection ? (connection.serverName ?? new URL(connection.serverUrl).host) : ""
        }?`}
      </MDSText>
      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          Mem Agent will immediately stop using this server. You can connect it again later.
        </MDSText>
      </MDSDialogTextContent>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton isDisabled={isDisconnecting} onClick={handleClose}>
          Cancel
        </MDSButton>
        <MDSButton
          variant={MDSButtonVariant.DangerFilled}
          isLoading={isDisconnecting}
          onClick={() => void handleDisconnect()}
        >
          Disconnect
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
});
